/**
 * NutritionFactsPanel — renders a Nutrition Facts label in one of four
 * regulatory styles, driven entirely by a structured `data` object from
 * the AI nutrition-lookup endpoint. Any field the source didn't publish
 * comes through as `null` and is hidden so the label never advertises
 * unknown values.
 *
 * Variants (matches customer reference images):
 *   - `fda_us`          — US FDA 2016 format (bold title, big Calories,
 *                          serving info block, %DV column, footnote)
 *   - `ca_simplified`   — Canadian English simplified panel
 *   - `ca_bilingual`    — Canadian French version of the simplified panel
 *   - `mini_horizontal` — Compact horizontal band for tiny labels
 *
 * The panel is fully self-contained (no framework theme colors) so
 * html2canvas exports render exactly what the user sees on screen.
 */

import React from 'react';

// ----------------------------- helpers ---------------------------------

const isPresent = (v) => v !== null && v !== undefined && v !== '';

const g  = (n) => (isPresent(n) ? `${Number(n).toString()} g`  : null);
const mg = (n) => (isPresent(n) ? `${Number(n).toString()} mg` : null);
const mc = (n) => (isPresent(n) ? `${Number(n).toString()} mcg` : null);
const dv = (n) => (isPresent(n) ? `${Number(n).toString()}%`   : null);

// -------------------------- FDA US variant -----------------------------

function FdaUsPanel({ data, sources }) {
  const d = data || {};
  const row = (label, value, indent = 0, dailyValue = null, bold = true) => {
    if (!isPresent(value)) return null;
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          borderBottom: '1px solid #000',
          padding: '2px 0',
          paddingLeft: indent * 12,
          fontSize: 12,
          lineHeight: 1.25,
        }}
      >
        <span>
          {bold ? <strong>{label}</strong> : label} {value}
        </span>
        {isPresent(dailyValue) && (
          <strong style={{ marginLeft: 8 }}>{Number(dailyValue)}%</strong>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        background: '#fff',
        color: '#000',
        border: '2px solid #000',
        padding: 8,
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        width: '100%',
      }}
    >
      <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1 }}>
        Nutrition Facts
      </div>
      {isPresent(d.servings_per_container) && (
        <div style={{ fontSize: 12, fontWeight: 700 }}>
          {d.servings_per_container} servings per container
        </div>
      )}
      {isPresent(d.serving_size) && (
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            borderBottom: '8px solid #000',
            paddingBottom: 2,
          }}
        >
          Serving size {d.serving_size}
        </div>
      )}

      {isPresent(d.calories) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            borderBottom: '4px solid #000',
            padding: '4px 0 2px',
          }}
        >
          <span style={{ fontSize: 22, fontWeight: 900 }}>Calories</span>
          <span style={{ fontSize: 28, fontWeight: 900, lineHeight: 1 }}>
            {d.calories}
          </span>
        </div>
      )}

      <div style={{ textAlign: 'right', fontSize: 10, fontWeight: 700, padding: '2px 0' }}>
        % Daily Value*
      </div>

      {row('Total Fat',      g(d.total_fat_g),      0, d.total_fat_dv)}
      {row('Saturated Fat',  g(d.saturated_fat_g),  1, d.saturated_fat_dv, false)}
      {row('Trans Fat',      g(d.trans_fat_g),      1, null,               false)}
      {row('Cholesterol',    mg(d.cholesterol_mg),  0, d.cholesterol_dv)}
      {row('Sodium',         mg(d.sodium_mg),       0, d.sodium_dv)}
      {row('Total Carbohydrate', g(d.total_carbs_g),0, d.total_carbs_dv)}
      {row('Dietary Fiber',  g(d.dietary_fiber_g),  1, d.dietary_fiber_dv, false)}
      {row('Total Sugars',   g(d.total_sugars_g),   1, null,               false)}
      {row('Includes Added Sugars', g(d.added_sugars_g), 2, d.added_sugars_dv, false)}
      {row('Protein',        g(d.protein_g),        0, null)}

      <div style={{ borderTop: '8px solid #000', margin: '2px 0' }} />

      {row('Vitamin D',  mc(d.vitamin_d_mcg), 0, d.vitamin_d_dv, false)}
      {row('Calcium',    mg(d.calcium_mg),    0, d.calcium_dv,   false)}
      {row('Iron',       mg(d.iron_mg),       0, d.iron_dv,      false)}
      {row('Potassium',  mg(d.potassium_mg),  0, d.potassium_dv, false)}

      <div
        style={{
          borderTop: '4px solid #000',
          marginTop: 2,
          paddingTop: 4,
          fontSize: 9,
          lineHeight: 1.35,
        }}
      >
        * The % Daily Value (DV) tells you how much a nutrient in a serving
        of food contributes to a daily diet. 2,000 calories a day is used
        for general nutrition advice.
      </div>
    </div>
  );
}

// ------------------- Canadian simplified (EN / FR) ---------------------

function CaSimplifiedPanel({ data, sources, lang = 'en' }) {
  const d = data || {};
  const t = lang === 'fr'
    ? {
        title: 'Valeur nutritive',
        per: 'pour',
        calories: 'Calories',
        dv: '% valeur\nquotidienne*',
        totalFat: 'Lipides',
        satFat: 'saturés',
        transFat: '+ trans',
        carb: 'Glucides',
        fiber: 'Fibres',
        sugars: 'Sucres',
        protein: 'Protéines',
        cholesterol: 'Cholestérol',
        sodium: 'Sodium',
        potassium: 'Potassium',
        calcium: 'Calcium',
        iron: 'Fer',
        footer: '*5% ou moins c\'est peu,\n15% ou plus c\'est beaucoup',
        source: 'Source :',
      }
    : {
        title: 'Nutrition Facts',
        per: 'Per',
        calories: 'Calories',
        dv: '% Daily\nValue*',
        totalFat: 'Fat',
        satFat: 'Saturated',
        transFat: '+ Trans',
        carb: 'Carbohydrate',
        fiber: 'Fibre',
        sugars: 'Sugars',
        protein: 'Protein',
        cholesterol: 'Cholesterol',
        sodium: 'Sodium',
        potassium: 'Potassium',
        calcium: 'Calcium',
        iron: 'Iron',
        footer: '*5% or less is a little,\n15% or more is a lot',
        source: 'Source:',
      };

  const rowSimple = (label, value, dvVal, bold = true, indent = 0) => {
    if (!isPresent(value)) return null;
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          borderBottom: '1px solid #000',
          padding: '2px 0',
          paddingLeft: indent * 10,
          fontSize: 11,
        }}
      >
        <span>{bold ? <strong>{label}</strong> : label} {value}</span>
        {isPresent(dvVal) && <span style={{ marginLeft: 6 }}>{Number(dvVal)} %</span>}
      </div>
    );
  };

  const servingLabel = [
    d.serving_size ? `${t.per} ${d.serving_size}` : null,
  ].filter(Boolean).join(' ');

  return (
    <div
      style={{
        background: '#fff',
        color: '#000',
        border: '2px solid #000',
        padding: 8,
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        width: '100%',
      }}
    >
      <div style={{ fontSize: 20, fontWeight: 900, lineHeight: 1 }}>{t.title}</div>
      {servingLabel && (
        <div style={{ fontSize: 11, borderBottom: '4px solid #000', paddingBottom: 2 }}>
          {servingLabel}
        </div>
      )}

      {isPresent(d.calories) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            borderBottom: '4px solid #000',
            padding: '3px 0',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 900 }}>
            {t.calories} {d.calories}
          </span>
          <span
            style={{
              fontSize: 8,
              fontWeight: 700,
              textAlign: 'right',
              whiteSpace: 'pre-line',
              lineHeight: 1.1,
            }}
          >
            {t.dv}
          </span>
        </div>
      )}

      {rowSimple(t.totalFat,     g(d.total_fat_g),     d.total_fat_dv)}
      {rowSimple(t.satFat,       g(d.saturated_fat_g), d.saturated_fat_dv, false, 1)}
      {rowSimple(t.transFat,     g(d.trans_fat_g),     null,                false, 1)}
      {rowSimple(t.carb,         g(d.total_carbs_g),   d.total_carbs_dv)}
      {rowSimple(t.fiber,        g(d.dietary_fiber_g), d.dietary_fiber_dv, false, 1)}
      {rowSimple(t.sugars,       g(d.total_sugars_g),  null,                false, 1)}
      {rowSimple(t.protein,      g(d.protein_g),       null)}
      {rowSimple(t.cholesterol,  mg(d.cholesterol_mg), null)}
      {rowSimple(t.sodium,       mg(d.sodium_mg),      d.sodium_dv)}
      {rowSimple(t.potassium,    mg(d.potassium_mg),   d.potassium_dv, false)}
      {rowSimple(t.calcium,      mg(d.calcium_mg),     d.calcium_dv,   false)}
      {rowSimple(t.iron,         mg(d.iron_mg),        d.iron_dv,      false)}

      <div style={{ fontSize: 8, lineHeight: 1.3, marginTop: 4, whiteSpace: 'pre-line' }}>
        {t.footer}
      </div>
    </div>
  );
}

// ---------------------------- Mini panel -------------------------------

function MiniPanel({ data, sources }) {
  const d = data || {};
  const parts = [
    isPresent(d.calories) ? `${d.calories} Cal` : null,
    isPresent(d.total_fat_g) ? `Fat ${d.total_fat_g}g` : null,
    isPresent(d.total_carbs_g) ? `Carbs ${d.total_carbs_g}g` : null,
    isPresent(d.total_sugars_g) ? `Sugars ${d.total_sugars_g}g` : null,
    isPresent(d.protein_g) ? `Protein ${d.protein_g}g` : null,
    isPresent(d.sodium_mg) ? `Sodium ${d.sodium_mg}mg` : null,
  ].filter(Boolean);

  return (
    <div
      style={{
        background: '#fff',
        color: '#000',
        border: '1.5px solid #000',
        padding: '4px 6px',
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        fontSize: 10,
        lineHeight: 1.25,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 2 }}>
        Nutrition Facts
        {isPresent(d.serving_size) && (
          <span style={{ fontWeight: 400 }}> — per {d.serving_size}</span>
        )}
      </div>
      <div>{parts.join(' · ') || 'No values available'}</div>
    </div>
  );
}

// ------------------------- Public dispatcher ---------------------------

export const NUTRITION_STYLES = [
  { value: 'fda_us',          label: 'FDA Standard (US)' },
  { value: 'ca_simplified',   label: 'Canadian Simplified (EN)' },
  { value: 'ca_bilingual',    label: 'Canadian Simplified (FR)' },
  { value: 'mini_horizontal', label: 'Mini Horizontal' },
];

export default function NutritionFactsPanel({
  data,
  style = 'fda_us',
  sources = [],
  lang = 'en',
}) {
  if (!data || Object.values(data).every((v) => v === null || v === undefined || v === '')) {
    return null;
  }
  let panel;
  if (style === 'ca_simplified')        panel = <CaSimplifiedPanel data={data} sources={sources} lang="en" />;
  else if (style === 'ca_bilingual')    panel = <CaSimplifiedPanel data={data} sources={sources} lang="fr" />;
  else if (style === 'mini_horizontal') panel = <MiniPanel        data={data} sources={sources} />;
  else                                  panel = <FdaUsPanel        data={data} sources={sources} />;

  return (
    <div>
      {panel}
      <NutritionSourcesNotice sources={sources} lang={lang} />
    </div>
  );
}

// ------------------------- Shared source notice -----------------------
// Rendered directly below every Nutrition Facts panel style so operators
// know the values came from AI-cited sources, not from a lab COA. Lists
// EVERY URL the AI returned — not just the first — so a regulator can
// audit each figure.

function NutritionSourcesNotice({ sources, lang = 'en' }) {
  if (!sources || sources.length === 0) return null;
  const label = lang === 'fr'
    ? 'Les valeurs nutritionnelles sont basées sur les sources citées suivantes :'
    : 'Nutritional facts are based on the following cited sources:';
  return (
    <div
      style={{
        marginTop: 6,
        padding: '6px 8px',
        border: '1px solid #b45309',
        background: '#fef3c7',
        color: '#78350f',
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        fontSize: 8,
        lineHeight: 1.4,
        borderRadius: 2,
      }}
      data-testid="lm-nutrition-sources"
    >
      <div style={{ fontWeight: 700, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
        {label}
      </div>
      <ul style={{ margin: 0, paddingLeft: 14, wordBreak: 'break-all' }}>
        {sources.map((u, i) => (
          <li key={i} style={{ marginBottom: 1 }}>
            <a
              href={u}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#78350f', textDecoration: 'underline' }}
              data-testid={`lm-nutrition-source-${i}`}
            >
              {u}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
